import { BRAND } from '@/lib/cms/config';

export interface PDPBreadcrumbTranslations {
  home: string;
  watches: string;
  collections?: string;
}

export interface PDPBreadcrumbItem {
  label: string;
  href?: string;
}

/**
 * Build breadcrumb items for watch PDP pages.
 *
 * Returns locale-agnostic paths for next-intl Link (locale prefix is added automatically).
 *
 * Gallet: Home > Collections > Collection
 * Breitling: Home > Watches > Collection
 * NFL products use "NFL" as the collection label and link to /watches/nfl-collection/
 */
export function generatePDPBreadcrumbs(
  collection: string,
  productName: string,
  translations: PDPBreadcrumbTranslations
): PDPBreadcrumbItem[] {
  const formatSlug = (slug: string) =>
    slug
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const isNFL = productName.includes('NFL');
  const collectionLabel = isNFL ? 'NFL' : formatSlug(collection);

  if (BRAND === 'gallet') {
    const collectionHref = isNFL ? '/watches/nfl-collection/' : `/collections/${collection}`;

    return [
      { label: translations.home, href: '/' },
      {
        label: translations.collections || 'Collections',
        href: '/collections',
      },
      { label: collectionLabel, href: collectionHref },
    ];
  }

  const collectionHref = isNFL ? '/watches/nfl-collection/' : `/watches/${collection}`;

  return [
    { label: translations.home, href: '/' },
    { label: translations.watches, href: '/watches' },
    { label: collectionLabel, href: collectionHref },
  ];
}

export interface StrapBreadcrumbTranslations {
  home: string;
  straps: string;
}

export interface StrapMaterialBreadcrumb {
  /** Canonical material slug for PLP links (e.g. `leather`, not localized `kalbsleder`). */
  slug: string;
  /** Localized customer-facing material label (e.g. `Kalbsleder`). */
  label: string;
}

/**
 * Build breadcrumb items for strap PDP pages.
 *
 * Returns locale-agnostic paths for next-intl Link (locale prefix is added automatically).
 *
 * Pattern: Home > Straps > Material
 */
export function generateStrapBreadcrumbs(
  material: StrapMaterialBreadcrumb,
  translations: StrapBreadcrumbTranslations
): PDPBreadcrumbItem[] {
  return [
    { label: translations.home, href: '/' },
    { label: translations.straps, href: '/straps' },
    { label: material.label, href: `/straps/${material.slug}` },
  ];
}
